import type { ReportMedia, ReportMovement, ReportSummary } from '@obras/shared';
import {
  extensionOf,
  formatBytes,
  formatCoordinate,
  formatCurrency,
  formatCurrencyDelta,
  formatDate,
  formatDateTime,
  formatDuration,
  REPORT_TYPE_META,
} from '@obras/shared';
import * as Icons from 'lucide-react';
import { FileText, Play } from 'lucide-react';

import { Badge } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { EmptyState, Skeleton } from '../../components/ui/Feedback.js';
import { Lightbox, useLightbox, type LightboxItem } from '../../components/ui/Lightbox.js';
import { cn } from '../../lib/cn.js';
import { BotaoBaixar, BotaoVer, Selo } from '../documentos/Arquivo.js';
import { VideoEmEspera } from './VideoEstado.js';

const ICON = { strokeWidth: 1.5 } as const;

/** Fundo do marcador na cor da movimentação — mapa fixo de design.md §3. */
const MARKER_TONE: Record<string, string> = {
  muted: 'bg-muted-foreground',
  success: 'bg-success',
  info: 'bg-info',
  danger: 'bg-danger',
  warning: 'bg-warning',
};

const DELTA_TONE: Record<string, string> = {
  muted: 'text-muted-foreground',
  success: 'text-success',
  info: 'text-info',
  danger: 'text-danger',
  warning: 'text-warning',
};

function MovementStrip({ movement }: { movement: ReportMovement }) {
  const meta = REPORT_TYPE_META[movement.type];
  const reversed = movement.reversedByMovementId !== null;

  const isTermChange = movement.days !== null;
  const isPayment = meta.effect === 'paid';

  const previous = isTermChange
    ? formatDate(movement.previousEndDate)
    : formatCurrency(isPayment ? movement.previousPaidCents : movement.previousValueCents);

  const next = isTermChange
    ? formatDate(movement.newEndDate)
    : formatCurrency(isPayment ? movement.newPaidCents : movement.newValueCents);

  const delta = isTermChange
    ? `+${movement.days} dia(s)`
    : formatCurrencyDelta(movement.amountCents);

  return (
    <div
      className={cn(
        'tabular mt-3 flex flex-wrap items-center gap-2 rounded-md bg-muted px-3 py-2 text-[13px]',
        reversed && 'opacity-70',
      )}
    >
      <span className={cn('flex items-center gap-1.5 font-medium', DELTA_TONE[meta.tone])}>
        {meta.label}
      </span>

      <span className={cn('text-muted-foreground', reversed && 'line-through')}>{previous}</span>
      <span className="text-muted-foreground" aria-hidden>
        →
      </span>
      <span className={cn('font-medium', reversed && 'text-muted-foreground line-through')}>
        {next}
      </span>
      <span className={cn('font-medium', DELTA_TONE[meta.tone], reversed && 'line-through')}>
        {delta}
      </span>

      {reversed ? (
        <Badge tone="muted" icon={Icons.Undo2} className="ml-auto">
          Estornado
        </Badge>
      ) : null}
    </div>
  );
}

function TypeMarker({ type }: { type: ReportSummary['type'] }) {
  const meta = REPORT_TYPE_META[type];
  const Component =
    (Icons as unknown as Record<string, Icons.LucideIcon>)[meta.icon] ?? Icons.FileText;

  return (
    <span
      className={cn(
        'relative z-10 grid size-7 shrink-0 place-items-center rounded-full',
        MARKER_TONE[meta.tone] ?? 'bg-muted-foreground',
      )}
    >
      <Component {...ICON} size={14} className="text-white" aria-hidden />
    </span>
  );
}

/**
 * Anexos da visita.
 *
 * Todas as fotos aparecem — o corte em seis com um "+N" mudo escondia
 * justamente o registro de campo que o relatório existe para carregar.
 * Documentos ficam separados: PDF se abre, foto e vídeo se olham.
 *
 * Vídeo e foto dividem a mesma grade e o mesmo visualizador. Separá-los faria
 * a pessoa ver metade da visita numa lista e metade em outra, quando o que ela
 * quer é percorrer o que foi registrado, na ordem em que foi registrado.
 */
/**
 * Uma miniatura da grade.
 *
 * O vídeo tem três estados e nenhum deles pode virar um quadrado cinza mudo:
 * processando (a capa ainda não existe), pronto (capa + duração + play) e
 * falhou (o Bunny recusou o arquivo).
 */
function MediaThumb({
  item,
  onOpen,
  label,
}: {
  item: ReportMedia;
  onOpen: () => void;
  label: string;
}) {
  const video = item.kind === 'video';

  if (video && item.status && item.status !== 'pronto') {
    return <VideoEmEspera mediaId={item.id} status={item.status} compacto />;
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      className="group relative block aspect-square w-full overflow-hidden rounded-sm bg-muted"
    >
      <img
        src={video ? item.thumbnailUrl : item.url}
        alt=""
        loading="lazy"
        className="size-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
      />

      {video ? (
        <>
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid size-9 place-items-center rounded-full bg-black/55 text-white">
              <Play size={15} strokeWidth={2} fill="currentColor" aria-hidden />
            </span>
          </span>
          {item.durationSeconds ? (
            <span className="tabular absolute right-1 bottom-1 rounded-xs bg-black/65 px-1 py-0.5 text-[10px] font-medium text-white">
              {formatDuration(item.durationSeconds)}
            </span>
          ) : null}
        </>
      ) : null}
    </button>
  );
}

function ReportAttachments({ report }: { report: ReportSummary }) {
  const lightbox = useLightbox();

  const visuais = report.media.filter((item) => item.kind === 'imagem' || item.kind === 'video');
  const documents = report.media.filter((item) => item.kind === 'arquivo');

  const slides: LightboxItem[] = visuais.map((item) => ({
    id: item.id,
    url: item.url,
    title: report.title,
    kind: item.kind === 'video' ? 'video' : 'imagem',
    thumbnailUrl: item.thumbnailUrl,
    meta: [
      formatDateTime(item.takenAt) !== '—'
        ? formatDateTime(item.takenAt)
        : formatDate(report.visitDate),
      item.durationSeconds ? formatDuration(item.durationSeconds) : null,
      item.latitude != null && item.longitude != null
        ? `${formatCoordinate(item.latitude, 'lat')} ${formatCoordinate(item.longitude, 'lng')}`
        : null,
      report.author.name,
    ].filter((line): line is string => Boolean(line)),
  }));

  if (report.media.length === 0) return null;

  return (
    <>
      {visuais.length > 0 ? (
        <ul className="mt-3 grid grid-cols-3 gap-1.5 sm:grid-cols-4 lg:grid-cols-6">
          {visuais.map((item, position) => (
            <li key={item.id}>
              <MediaThumb
                item={item}
                onOpen={() => lightbox.open(position)}
                label={`Ver ${item.kind === 'video' ? 'vídeo' : 'foto'} ${position + 1} de ${visuais.length}`}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {/*
       * Um arquivo por linha, com nome, formato e tamanho — a mesma linha do
       * dossiê. Antes cada um era um botão "Documento", e quem lia não sabia
       * se o segundo era a planilha ou o laudo. O download passa pela API, que
       * devolve o arquivo com o nome que ele tinha.
       */}
      {documents.length > 0 ? (
        <ul className="mt-3 divide-y divide-border">
          {documents.map((document) => {
            const extension = extensionOf(document.originalName);

            return (
              <li key={document.id} className="flex items-center gap-3 py-2">
                <Selo extension={extension} />

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium">
                    {document.originalName}
                  </span>
                  <span className="block text-[12px] text-muted-foreground">
                    {formatBytes(document.sizeBytes)}
                  </span>
                </span>

                <BotaoVer
                  url={document.url}
                  extension={extension}
                  originalName={document.originalName}
                  pequeno
                />
                <BotaoBaixar
                  obraId={report.obraId}
                  documentId={document.id}
                  originalName={document.originalName}
                  pequeno
                />
              </li>
            );
          })}
        </ul>
      ) : null}

      <Lightbox
        items={slides}
        index={lightbox.index}
        onClose={lightbox.close}
        onNavigate={lightbox.navigate}
      />
    </>
  );
}

/**
 * A tarja do relatório removido.
 *
 * Quem removeu, quando e por quê — as três juntas, porque um registro riscado
 * sem motivo é pior que registro nenhum: quem lê meses depois não sabe se
 * aquilo foi engano de digitação ou se o fato nunca aconteceu.
 */
function TarjaDeRemocao({
  deletion,
  mediaCount,
}: {
  deletion: NonNullable<ReportSummary['deletion']>;
  mediaCount: number;
}) {
  return (
    <div className="mt-3 rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium">
        <Icons.Trash2 {...ICON} size={14} aria-hidden />
        Removido em {formatDate(deletion.at)}
        {deletion.by ? <span className="font-normal">por {deletion.by.name}</span> : null}
      </p>
      {deletion.reason ? (
        <p className="mt-1 leading-relaxed whitespace-pre-line">{deletion.reason}</p>
      ) : null}
      {/* Os anexos seguem o relatório: somem com ele e voltam se for restaurado. */}
      {mediaCount > 0 ? (
        <p className="mt-1 leading-relaxed">
          {mediaCount === 1 ? '1 anexo oculto' : `${mediaCount} anexos ocultos`} com o relatório.
          Voltam se ele for restaurado.
        </p>
      ) : null}
    </div>
  );
}

function ReportEntry({
  report,
  onRemove,
  onRestore,
}: {
  report: ReportSummary;
  onRemove?: ((report: ReportSummary) => void) | undefined;
  onRestore?: ((report: ReportSummary) => void) | undefined;
}) {
  const removido = report.deletion !== null;

  return (
    <li className="flex gap-4">
      <TypeMarker type={report.type} />

      {/*
        O removido fica em meio-tom: continua legível, mas não disputa a leitura
        com o que de fato vale no histórico da obra.
      */}
      <div className={cn('min-w-0 flex-1 pb-6', removido && 'opacity-60')}>
        <p className="tabular mb-1.5 flex flex-wrap items-center gap-2 text-[13px]">
          <span className={cn('font-medium', removido && 'line-through')}>
            {formatDate(report.visitDate)}
          </span>
          <span className="text-muted-foreground">por {report.author.name}</span>
          {report.status === 'rascunho' ? <Badge tone="muted">Rascunho</Badge> : null}
          {removido ? <Badge tone="danger">Removido</Badge> : null}
          {report.onBehalfOf ? (
            <Badge tone="primary" icon={Icons.UserCog}>
              Em substituição a {report.onBehalfOf.name}
            </Badge>
          ) : null}
        </p>

        <Card className="p-4">
          <div className="flex items-start justify-between gap-3">
            <h3
              className={cn(
                'text-[15px] font-semibold tracking-tight',
                removido && 'line-through decoration-2',
              )}
            >
              {report.title}
            </h3>

            {onRemove && !removido ? (
              <button
                type="button"
                onClick={() => onRemove(report)}
                title={`Remover ${report.title}`}
                aria-label={`Remover ${report.title}`}
                className="-m-1 grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-danger"
              >
                <Icons.Trash2 {...ICON} size={16} aria-hidden />
              </button>
            ) : null}

            {onRestore && removido ? (
              <button
                type="button"
                onClick={() => onRestore(report)}
                title={`Restaurar ${report.title}`}
                aria-label={`Restaurar ${report.title}`}
                className="-m-1 grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Icons.ArchiveRestore {...ICON} size={16} aria-hidden />
              </button>
            ) : null}
          </div>

          {report.description ? (
            <p
              className={cn(
                'mt-2 max-w-[68ch] leading-relaxed whitespace-pre-line text-muted-foreground',
                removido && 'line-through',
              )}
            >
              {report.description}
            </p>
          ) : null}

          {report.movement ? <MovementStrip movement={report.movement} /> : null}

          {report.deletion ? (
            <TarjaDeRemocao deletion={report.deletion} mediaCount={report.mediaCount} />
          ) : null}

          <ReportAttachments report={report} />
        </Card>
      </div>
    </li>
  );
}

export function ReportTimeline({
  reports,
  loading,
  emptyAction,
  onRemove,
  onRestore,
}: {
  reports: readonly ReportSummary[];
  loading: boolean;
  emptyAction?: React.ReactNode;
  /** Quando ausente, a linha do tempo não oferece a remoção. */
  onRemove?: ((report: ReportSummary) => void) | undefined;
  /** Idem para restaurar um relatório removido. */
  onRestore?: ((report: ReportSummary) => void) | undefined;
}) {
  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="flex gap-4">
            <Skeleton className="size-7 rounded-full" />
            <div className="flex-1">
              <Skeleton className="mb-2 h-3.5 w-40" />
              <Skeleton className="h-28 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={FileText}
          title="Nenhum relatório registrado"
          description="Registre a primeira visita técnica para começar o acompanhamento desta obra."
          action={emptyAction}
        />
      </Card>
    );
  }

  return (
    // O fio vertical liga os eventos: é conector de leitura, não moldura.
    <ol className="relative before:absolute before:top-2 before:bottom-2 before:left-[13px] before:w-px before:bg-border">
      {reports.map((report) => (
        <ReportEntry key={report.id} report={report} onRemove={onRemove} onRestore={onRestore} />
      ))}
    </ol>
  );
}
